// src/pages/LoginPage.tsx
import React, { useState, useRef } from 'react';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Box, TextField, Typography, Paper } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import PasswordField from '../components/common/PasswordField';
import { SignInButton } from '../components/common/Buttons';
import { colors } from '../theme/theme';
import { InlineMessage } from '../components/common/InlineMessage';

import {
  formValidators,
  isFormValid,
  FormErrors,
} from '../utils/validationHelpers';

import { useErrorHandler, ErrorDisplayType } from '../errors';

const LoginContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '100vh',
  backgroundColor:
    theme.palette.mode === 'dark' ? colors.grey[800] : colors.grey[300],
  paddingBottom: '120px',

  '@media (max-width: 768px)': {
    paddingBottom: '8rem',
  },
}));

const LoginPaper = styled(Paper)({
  padding: '1rem',
  width: '100%',
  maxWidth: '400px',
  borderRadius: '12px',
  border: `2px solid ${colors.primary.main}`,
  boxShadow: '0 8px 14px rgba(0,0,0,0.25)',

  '@media (max-width: 768px)': {
    padding: '1rem 1rem',
    maxWidth: '90%',
    borderRadius: '12px',
  },
});

interface LoginFormData {
  username: string;
  password: string;
}

type LoginFormErrors = FormErrors<LoginFormData>;

// Shape of the location a ProtectedRoute redirect carries in state.from.
// Only the fields navigate() actually needs are declared.
interface RedirectLocationState {
  from?: {
    pathname: string;
    search?: string;
    hash?: string;
  };
}

// Guards against an open-redirect via a crafted `?from=` query value (e.g.
// a shared link like /login?from=https://evil.example.com or
// /login?from=//evil.example.com). Only ever navigates to a same-origin
// relative path; anything else is ignored and falls back to '/'.
// This check only applies to the query-param source (axiosInterceptor's
// hard-redirect path) — location.state.from is never attacker-controlled,
// since it only ever holds a real in-app Location object set by our own
// ProtectedRoute, so it doesn't need the same scrutiny.
const isSafeRelativePath = (path: string): boolean => {
  if (!path.startsWith('/') || path.startsWith('//')) return false;
  if (path.includes('\\')) return false;
  if (/^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(path)) return false;
  return true;
};

const fieldSx = (theme: any) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 0,
    '& fieldset': { borderRadius: '2px' },
  },
  '& .MuiInputLabel-root:not(.MuiInputLabel-shrink)': {
    fontSize: '1.6rem',
    fontWeight: 500,
    transform: 'translate(14px, 10px) scale(1)',
  },
  '& .MuiInputLabel-root.MuiInputLabel-shrink': {
    fontSize: '1rem',
    fontWeight: 600,
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: colors.primary.main,
  },
  '& .MuiInputLabel-root.Mui-error': {
    color:
      theme.palette.mode === 'dark'
        ? theme.palette.primary.light
        : colors.primary.main,
  },
  '& .MuiFormHelperText-root.Mui-error': {
    color: theme.palette.mode === 'dark' ? '#ff6b6b' : '#a63f59',
    fontWeight: 600,
    fontSize: '1.6rem',
  },
  '@media (max-width: 768px)': {
    marginTop: '1rem',
    marginBottom: '1rem',
  },
});

const LoginPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const { handleError } = useErrorHandler();

  const usernameRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState<LoginFormData>({
    username: '',
    password: '',
  });

  const [errors, setErrors] = useState<LoginFormErrors>({});
  const [authError, setAuthError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const validateForm = (): boolean => {
    const newErrors: LoginFormErrors = {};

    const usernameError = formValidators.login.username(formData.username);
    if (usernameError) newErrors.username = usernameError;

    const passwordError = formValidators.login.password(formData.password);
    if (passwordError) newErrors.password = passwordError;

    setErrors(newErrors);

    if (newErrors.username && usernameRef.current) {
      usernameRef.current.focus();
    } else if (newErrors.password && passwordRef.current) {
      passwordRef.current.focus();
    }

    return isFormValid(newErrors);
  };

  const handleInputChange = (field: keyof LoginFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
    if (authError) setAuthError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError('');

    if (!validateForm()) {
      handleError(new Error('Login validation failed'), {
        context: 'login validation',
        displayType: ErrorDisplayType.INLINE,
        showToast: false,
      });
      return;
    }

    setIsLoading(true);

    try {
      const result = await login(formData.username, formData.password);

      if (result.success) {
        // Priority: an in-app ProtectedRoute redirect (location.state.from,
        // trusted) beats a hard-redirect query param (?from=, validated
        // before use) beats the default landing page.
        const stateFrom = (location.state as RedirectLocationState | null)
          ?.from;
        const queryFrom = new URLSearchParams(location.search).get('from');
        const safeQueryFrom =
          queryFrom && isSafeRelativePath(queryFrom) ? queryFrom : null;

        navigate(stateFrom ?? safeQueryFrom ?? '/');
      } else {
        setAuthError(
          result.message || 'Invalid username or password. Please try again.'
        );
        handleError(new Error('Login failed'), {
          context: 'authentication',
          displayType: ErrorDisplayType.INLINE,
          showToast: false,
        });
      }
    } catch (err: any) {
      // fetch throws TypeError on network failure — no err.response
      let errorMessage = 'An error occurred. Please try again.';

      if (err instanceof TypeError || err?.name === 'TypeError') {
        errorMessage =
          'Unable to reach the server. Please check your connection and try again.';
      } else if (err?.response?.status === 401) {
        errorMessage = 'Invalid username or password.';
      } else if (err?.response?.status === 403) {
        errorMessage = 'Access denied. Please contact administrator.';
      } else if (err?.response?.status >= 500) {
        errorMessage = 'Server error. Please try again later.';
      }

      setAuthError(errorMessage);
      handleError(err, {
        context: 'login request',
        displayType: ErrorDisplayType.INLINE,
        showToast: false,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <LoginContainer>
      <LoginPaper elevation={3}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography
            variant="h4"
            component="h1"
            sx={{
              userSelect: 'none',
              pointerEvents: 'none',
              color:
                theme.palette.mode === 'dark'
                  ? theme.palette.primary.light
                  : 'inherit',
              '@media (max-width: 768px)': { fontSize: '1.8rem' },
            }}
          >
            Villa Siesta Pet Retreat
          </Typography>
          <Typography
            color="text.secondary"
            sx={(theme) => ({
              color:
                theme.palette.mode === 'dark'
                  ? colors.grey[300]
                  : colors.grey[800],
              fontSize: '1.8rem',
              userSelect: 'none',
              pointerEvents: 'none',
              '@media (max-width: 768px)': { fontSize: '1.8rem' },
            })}
          >
            Please sign in
          </Typography>
        </Box>

        {authError && <InlineMessage message={authError} severity="error" />}

        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Username"
            variant="outlined"
            margin="normal"
            value={formData.username}
            onChange={(e) => handleInputChange('username', e.target.value)}
            error={Boolean(errors.username)}
            helperText={errors.username}
            disabled={isLoading}
            autoFocus
            autoComplete="username"
            inputRef={usernameRef}
            sx={fieldSx}
          />
          <PasswordField
            fullWidth
            label="Password"
            variant="outlined"
            margin="normal"
            value={formData.password}
            onChange={(e) => handleInputChange('password', e.target.value)}
            error={Boolean(errors.password)}
            helperText={errors.password}
            disabled={isLoading}
            autoComplete="current-password"
            inputRef={passwordRef}
            sx={fieldSx}
          />
          <Box sx={{ textAlign: 'right', mb: 1 }}>
            <RouterLink
              to="/forgot-password"
              style={{
                fontSize: '1.4rem',
                color: colors.primary.main,
                textDecoration: 'none',
              }}
            >
              Forgot password?
            </RouterLink>
          </Box>
          <SignInButton isLoading={isLoading} disabled={isLoading} />
        </form>
      </LoginPaper>
    </LoginContainer>
  );
};

export default LoginPage;
